import { useState, useEffect, useCallback } from 'react'

/**
 * SupportModal -- Submit a new support request.
 *
 * Matches legacy: supportRequest.tpl.html
 */

export interface SupportModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: { subject: string; date: string; description: string }) => void
}

export function SupportModal({ isOpen, onClose, onSubmit }: SupportModalProps) {
    const todayStr = new Date().toISOString().split('T')[0]

    const [subject, setSubject] = useState('')
    const [date, setDate] = useState(todayStr)
    const [description, setDescription] = useState('')

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSubject('')
            setDate(todayStr)
            setDescription('')
        }
    }, [isOpen, todayStr])

    const isFormValid = subject.trim().length > 0 && date !== '' && description.trim().length > 0

    const handleSubmit = () => {
        if (!isFormValid) return
        onSubmit({
            subject: subject.trim(),
            date,
            description: description.trim(),
        })
        onClose()
    }

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        },
        [onClose],
    )

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            document.body.classList.add('modal-open')
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.classList.remove('modal-open')
        }
    }, [isOpen, handleKeyDown])

    if (!isOpen) return null

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose()
    }

    return (
        <>
            <div className="aps-background-mask mask-is-active" />
            <div
                className="modal fade in"
                style={{ display: 'block' }}
                role="dialog"
                tabIndex={-1}
                onClick={handleBackdropClick}
            >
                <div className="modal-dialog modal-md">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                            <h4 className="modal-title">Submit Support Request</h4>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="support-subject">
                                    Subject <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="support-subject"
                                    type="text"
                                    className="form-control"
                                    maxLength={128}
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Enter subject..."
                                    autoFocus
                                />
                                <span className="help-block text-muted">{subject.length}/128 characters</span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="support-date">
                                    Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="support-date"
                                    type="date"
                                    className="form-control"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="support-description">
                                    Description <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    id="support-description"
                                    className="form-control"
                                    rows={5}
                                    maxLength={2000}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Describe the issue..."
                                />
                                <span className="help-block text-muted">{description.length}/2000 characters</span>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-default" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={!isFormValid}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
